import { Link } from "@tanstack/react-router";
import { Avatar } from "@/components/Avatar";

export type MemberCardMember = {
  id: string;
  name: string;
  email: string;
  role: string;
  online?: boolean;
  openCount?: number;
  completedCount?: number;
  projectCount?: number;
  load?: "light" | "balanced" | "heavy";
  projects?: { id: string; color: string }[];
};

export type MemberCardProps = { member: MemberCardMember };

/**
 * MemberCard — mirrors `.mcard` in designs/Team.html.
 *
 * The workload row uses a bare `.progress` (not the `.progress-lab` wrapper
 * that ProgressBar produces) so that we can colour the bar by load tier.
 * Bar percent is derived from the load tier since the API doesn't return
 * a raw percentage: light=30, balanced=55, heavy=80.
 */
export function MemberCard({ member: m }: MemberCardProps) {
  const loadStyle =
    m.load === "heavy"
      ? { pct: 80, klass: "wl-high", bar: "var(--st-danger)" }
      : m.load === "balanced"
        ? { pct: 55, klass: "wl-mid", bar: "var(--orange-500)" }
        : m.load === "light"
          ? { pct: 30, klass: "wl-low", bar: "var(--green-500)" }
          : null;

  const loadLabel =
    m.load === "heavy"
      ? "Heavy"
      : m.load === "balanced"
        ? "Balanced"
        : m.load === "light"
          ? "Light"
          : null;

  return (
    <Link
      to="/team/$userId"
      params={{ userId: m.id }}
      className="mcard"
      style={{ display: "block", textDecoration: "none", color: "inherit" }}
    >
      <div className="mc-top">
        <div className="mc-ava">
          <Avatar id={m.id} name={m.name} size={48} />
          {m.online ? (
            <span
              className="pres"
              aria-label="online"
              style={{ background: "var(--green-500)" }}
            />
          ) : null}
        </div>
        <div>
          <div className="mc-name">{m.name}</div>
          <div className="mc-role">{m.role}</div>
          <div className="mc-email">{m.email}</div>
        </div>
      </div>

      <div className="mc-stats">
        <div className="mc-stat">
          <div className="v">{m.openCount ?? 0}</div>
          <div className="l">Open tasks</div>
        </div>
        <div className="mc-stat">
          <div className="v">{m.completedCount ?? 0}</div>
          <div className="l">Completed</div>
        </div>
        <div className="mc-stat">
          <div className="v">{m.projectCount ?? 0}</div>
          <div className="l">Projects</div>
        </div>
      </div>

      {loadStyle && loadLabel ? (
        <div className="mc-wl">
          <div className="wlh">
            <span className="lab">Workload</span>
            <span className={`val ${loadStyle.klass}`}>{loadLabel}</span>
          </div>
          <div
            className="progress"
            role="progressbar"
            aria-valuenow={loadStyle.pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Workload"
          >
            <i
              style={{ width: `${loadStyle.pct}%`, background: loadStyle.bar }}
            />
          </div>
        </div>
      ) : null}

      <div className="mc-foot">
        <div className="mc-projs">
          {m.projects && m.projects.length > 0 ? (
            <>
              <div className="pdots">
                {m.projects.slice(0, 4).map((p) => (
                  <span key={p.id} style={{ background: p.color }} />
                ))}
              </div>
              <span className="ptxt">
                {m.projectCount ?? m.projects.length} active
              </span>
            </>
          ) : (
            <span className="ptxt">{m.projectCount ?? 0} active</span>
          )}
        </div>
        <span className="mc-assign" aria-hidden="true">
          View profile →
        </span>
      </div>
    </Link>
  );
}
